import { useState, useEffect, ChangeEventHandler, FC, SetStateAction, Dispatch, useContext } from 'react';
import { Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import axios from 'axios';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { MAX_PAGE_LIMIT } from '../../constants/numbers';
import { IUser } from '../../interfaces/user';
import IResponse, { IPagination } from '../../interfaces/response';
import BasicModal from './BasicModal';
import { LoadingButton } from '@mui/lab';
import { ICampaign } from '../../interfaces/campaign';
import BaranDataGrid from './BaranDataGrid';
import CategoryModal from '../admin/campaign/CategoryModal';
import RecommendHourModal from '../admin/campaign/RecommendHourModal';
import { CampaignContext } from '../../contexts/CampaignContext';
import ExportButton from './ExportButton';
import { GetApp } from '@mui/icons-material';

interface CampaginTableProps {
	tabItem: any;
	openModal?: boolean;
	dataEdit?: any;
	exportData?: number[];
	setDataEdit?: Dispatch<SetStateAction<any>>;
	setOpenModal?: Dispatch<SetStateAction<boolean>>;
	modalTitle?: string;
}

let status_list = {
	pending_content: 'در انتظار محتوا',
	payment_pend: 'در انتظار پرداخت',
	confirmation_pend: 'در انتظار تایید',
	queue_up: 'در صف انجام',
	in_progress: 'در حال انجام',
	completed: 'انجام شده',
	rejected: 'رد شده',
};

const CampaginTable: FC<CampaginTableProps> = ({ exportData, tabItem, dataEdit = null, setDataEdit = () => {}, setOpenModal = () => {}, openModal = false, modalTitle }) => {
	let { fetchedMedias } = useContext(CampaignContext);
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [fetchedData, setFetchedData] = useState<ICampaign[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [loading, setLoading] = useState(true);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [deleteUser, setDeleteUser] = useState<IUser>();
	const [refresh, setRefresh] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [data, setData] = useState(fetchedData);
	const [checked, setChecked] = useState<boolean>(false);
	const [category, setCategory] = useState<string>('');
	const [media, setMedia] = useState<number>(1);
	const [mediaType, setMediaType] = useState<string>('');
	const [id, setId] = useState<number>();
	const [loadingExport, setLoadingExport] = useState<{
		excelLoading: boolean;
	}>({
		excelLoading: false,
	});

	const handleChangeSearch: ChangeEventHandler<HTMLInputElement> = e => {
		setSearchTerm(e.target.value);
	};

	// useEffect(() => {

	// }, [category])

	useEffect(() => {
		if (dataEdit) {
			setChecked(dataEdit.check);
			setMedia(dataEdit.media_id);
			setMediaType(dataEdit.media_type);
			setCategory(dataEdit.title);
			setId(dataEdit.id);
		}
	}, [dataEdit]);

	useEffect(() => {
		if (searchTerm.length === 0) {
			setData(fetchedData);
		} else {
			const filterData = fetchedData.filter(item => item.title.includes(searchTerm));
			setData(filterData);
		}
	}, [searchTerm]);

	const handleFetchData = () => {
		axios<IResponse<ICampaign[]>>({
			url: tabItem.api && tabItem.api,
			params: {
				limit: pageSize,
				search: tabItem.search,
				status: tabItem.status,
				status_array: tabItem.status_array,
				page: page,
				role_id: tabItem.role_id,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
					setData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();

	const handleCreate = () => {
		axios<IResponse<boolean>>({
			url: tabItem.postApi && dataEdit?.action === 'edit' ? `${tabItem.postApi}/${id}` : `${tabItem.postApi}`,
			method: dataEdit && dataEdit?.action === 'edit' ? 'put' : 'post',
			data: {
				title: category,
				system_name: tabItem.system_name,
				is_active: checked,
			},
		}).then(res => {
			if (res.data.success) {
				setOpenModal(false);
				handleFetchData();
				if (dataEdit.action && dataEdit?.action === 'edit') {
					setDataEdit({});
				}
			}
		});
	};

	const handleCreateRecommendHour = () => {
		let filterRecommendHour = fetchedMedias?.find(item => item.id === media);
		axios<IResponse<boolean>>({
			url: tabItem.postApi && dataEdit?.action === 'edit' ? `${tabItem.postApi}/${id}` : `${tabItem.postApi}`,
			method: dataEdit && dataEdit.action && dataEdit?.action === 'edit' ? 'put' : 'post',
			data: {
				title: category,
				media_type: mediaType,
				media_id: filterRecommendHour?.id,
			},
		}).then(res => {
			if (res.data.success) {
				setOpenModal(false);
				handleFetchData();
			}
		});
	};

	const handleDelete = (id: number) => () => {
		axios<IResponse<boolean>>({
			url: tabItem.postApi && `${tabItem.postApi}/${id}`,
			method: 'DELETE',
		}).then(res => {
			if (res.data.success) {
				handleFetchData();
			}
		});
	};

	const handleChange = (event: SelectChangeEvent, id: number) => {
		axios<IResponse<ICampaign[]>>({
			url: `/admin/campaigns/update-status/${id}`,
			method: 'put',
			params: {
				status: event.target.value,
				settlement_later: event.target.value === 'confirmation_pend' ? 1 : '',
			},
		})
			.then(res => {
				if (res.data.success) {
					handleFetchData();
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const statusColumn: GridColDef<ICampaign>[] = [
		{
			field: 'change_status',
			headerName: t('campaign_type'),
			width: 150,
			flex: 1,
			display: 'flex',
			headerClassName: `${headerClassNames}`,
			headerAlign: 'center',
			align: 'center',
			renderCell: (params: any) => {
				let data = params.row;
				return (
					<FormControl sx={{ m: 1, minWidth: 120 }} size="small">
						<InputLabel id="select_statas">{t('select_statas')}</InputLabel>
						<Select labelId="select_statas" id="select_statas" label={t('select_statas')} onChange={(e: SelectChangeEvent) => handleChange(e, data.id)}>
							{Object.entries(status_list).map(([key, value]) => (
								<MenuItem value={key}>{value}</MenuItem>
							))}
						</Select>
					</FormControl>
				);
			},
		},
	];

	const columns: GridColDef<ICampaign>[] = [
		{
			field: 'action',
			headerName: t('action'),
			flex: 1,
			headerClassName: headerClassNames,
			headerAlign: 'center',
			align: 'center',
			renderCell: (params: any) => {
				let data = params.row;
				return (
					<>
						{tabItem.view && (
							<IconButton onClick={() => tabItem.viewAction(data.id)}>
								<VisibilityIcon className="text-text-secondary" />
							</IconButton>
						)}
						{tabItem.edit && (
							<IconButton onClick={() => tabItem.editAction(data.id, data.status)}>
								<EditIcon className="text-text-secondary" />
							</IconButton>
						)}
						{tabItem.delete && (
							<IconButton onClick={handleDelete(data.id)}>
								<DeleteOutlineIcon className="text-text-secondary" />
							</IconButton>
						)}
					</>
				);
			},
		},
	];

	let CombinationColumns;
	if (tabItem.statusChange) {
		CombinationColumns = [...tabItem.columns, ...statusColumn, ...columns];
	} else {
		CombinationColumns = [...tabItem.columns, ...columns];
	}

	useEffect(() => {
		setLoading(true);
		handleFetchData();
	}, [page, pageSize, refresh]);

	const handleCloseDeleteDialog = () => {
		setDeleteUser(undefined);
	};

	const handleDeleteUser = () => {
		setDeleteLoading(true);
		axios<IResponse<any>>({
			url: tabItem.api && `${tabItem.api}/${deleteUser?.id}`,
			method: 'DELETE',
		})
			.then(res => {
				if (res.data.success) {
					setDeleteUser(undefined);
					setRefresh(prev => prev + 1);
				}
			})
			.finally(() => {
				setDeleteLoading(false);
			});
	};

	const handleExportExcel = async () => {
		setLoadingExport(prev => ({ ...prev, excelLoading: true }));
		// exportToExcel({ data: exportData ?? [], fileName: 'campaign', t });
		axios<IResponse<any>>({
			url: '/campaigns/export',
			method: 'GET',
			params: {
				campaign_ids: JSON.stringify(exportData),
			},
		}).finally(() => {
			setLoadingExport(prev => ({ ...prev, excelLoading: false }));
		});
	};

	// const selectAllData = () => {
	// 	if (exportData?.length > 0) {
	// 		setCampaignItem([...data]);
	// 	} else {
	// 		setCampaignItem([]);
	// 	}
	// };

	return (
		<div className={'w-full'}>
			{tabItem?.isExportExcel && (
				<Box display={'flex'} gap={1} justifyContent={'end'} mb={0}>
					{/* <Button onClick={() => selectAllData()} variant="outlined">
						انتخاب همه
					</Button> */}
					<ExportButton loading={loadingExport?.excelLoading} color="primary" onClick={handleExportExcel} icon={<GetApp />}>
						خروجی اکسل
					</ExportButton>
				</Box>
			)}
			<BaranDataGrid<ICampaign> isCreate={tabItem.showBtn} loading={loading} pagination={pagination} rows={data} columns={CombinationColumns} pageSize={pageSize} setPageSize={setPageSize} searchTerm={searchTerm} handleSearch={handleChangeSearch} page={page} setPage={setPage} onClick={tabItem.clickAction} actionButtonLable={t(tabItem.label)} />
			{tabItem.label !== 'medias' && <CategoryModal setOpen={setOpenModal} openModal={openModal} label={modalTitle ? modalTitle : ''} setChecked={setChecked} title={category} checked={checked} setTitle={setCategory} handleCreate={handleCreate} />}
			{tabItem.system_name === 'campaign-release-time' && <RecommendHourModal setOpen={setOpenModal} openModal={openModal} label={tabItem.label} setMediaType={setMediaType} mediaType={mediaType} setMedia={setMedia} title={category} media={media} setTitle={setCategory} handleCreate={handleCreateRecommendHour} />}
			<BasicModal
				open={!!deleteUser}
				onClose={handleCloseDeleteDialog}
				title={t('delete_role')}
				titleClassName={'text-center grow'}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-sm rounded-lg'}
				showBackIcon={false}
				action={
					<>
						<Button onClick={handleCloseDeleteDialog} color="inherit">
							{t('cancel')}
						</Button>
						<LoadingButton loading={deleteLoading} onClick={handleDeleteUser} variant="outlined" color="error">
							{t('delete')}
						</LoadingButton>
					</>
				}
			>
				<Typography>{t('delete_user_description', { user: deleteUser?.name })}</Typography>
			</BasicModal>
		</div>
	);
};

export default CampaginTable;
